// CL-2 repairOrphanedToolCalls (omo F-9 영감).
//
// OpenAI 400 "tool_calls must be followed by tool messages responding to each tool_call_id"
// orphan 복구 — assistant + tool_calls 직후 미매칭 id에 합성 tool message 주입.
//
// 의존성: CL-1 errorClassifier ERROR_TYPES + RECOVERY_TABLE registerRecoveryHandler
//
// 사용 시점:
//   1. preflight — 매 OpenAI 호출 직전 1회 호출 (pure repair, 새 배열 반환)
//   2. recovery handler — CL-1 dispatchRecovery(ERROR_TYPES.TOOL_RESULT_MISSING, ctx) 분기
//      (registerOrphanRecoveryHandler 명시 호출로 등록, 자동 register X)
//
// idempotent — 이미 매칭된 tool_call_id는 건드리지 않음. 두 번 호출해도 동일 결과.

import { ERROR_TYPES } from './errorClassifier.js';
import { registerRecoveryHandler } from './RECOVERY_TABLE.js';

const ORPHAN_TOOL_RESULT_CONTENT = '{"error":"interrupted"}';

/**
 * messages[] 스캔 → orphan tool_call_id에 합성 tool message 주입.
 * 원본 mutation 0 — 새 배열 반환.
 *
 * @param {Array<object>} messages — OpenAI messages 배열
 * @returns {{messages: Array<object>, injectedCount: number, orphanIds: string[]}}
 */
export function repairOrphanedToolCalls(messages) {
  if (!Array.isArray(messages)) {
    return { messages, injectedCount: 0, orphanIds: [] };
  }

  const out = [];
  const orphanIds = [];
  let injectedCount = 0;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    out.push(msg);

    if (
      msg &&
      msg.role === 'assistant' &&
      Array.isArray(msg.tool_calls) &&
      msg.tool_calls.length > 0
    ) {
      // assistant + tool_calls 발견 — expectedIds 수집
      const expectedIds = new Set();
      const orderedIds = [];
      for (const tc of msg.tool_calls) {
        if (tc && typeof tc.id === 'string' && tc.id) {
          if (!expectedIds.has(tc.id)) {
            expectedIds.add(tc.id);
            orderedIds.push(tc.id);
          }
        }
      }

      // 직후 연속 tool role 메시지 스캔 — tool_call_id로 매칭 → expectedIds 제거
      let j = i + 1;
      while (j < messages.length && messages[j] && messages[j].role === 'tool') {
        const tcId = messages[j].tool_call_id;
        if (tcId) expectedIds.delete(tcId);
        out.push(messages[j]);
        j++;
      }

      // 남은 orphan ids — 원래 tool_calls 순서대로 주입
      for (const orphanId of orderedIds) {
        if (expectedIds.has(orphanId)) {
          out.push({
            role: 'tool',
            tool_call_id: orphanId,
            content: ORPHAN_TOOL_RESULT_CONTENT,
          });
          orphanIds.push(orphanId);
          injectedCount++;
        }
      }

      // i를 j-1로 점프 (다음 loop의 i++ → j)
      i = j - 1;
    }
  }

  return { messages: out, injectedCount, orphanIds };
}

/**
 * CL-1 RECOVERY_TABLE에 tool_result_missing handler 등록.
 *
 * caller(boot path)가 명시 호출. import만으로는 register 안 됨 (부수효과 0).
 *
 * handler 동작:
 *   - ctx.messages array를 in-place mutation (caller 참조 보존)
 *   - orphan ≥ 1 → true (recovery 성공)
 *   - orphan 0 → false (recover 불가)
 *   - ctx 또는 ctx.messages 누락 → false
 */
export function registerOrphanRecoveryHandler() {
  return registerRecoveryHandler(ERROR_TYPES.TOOL_RESULT_MISSING, async (ctx) => {
    if (!ctx || !Array.isArray(ctx.messages)) return false;
    const result = repairOrphanedToolCalls(ctx.messages);
    if (result.injectedCount === 0) return false;
    // in-place mutation — caller 참조 보존
    ctx.messages.length = 0;
    for (const m of result.messages) ctx.messages.push(m);
    return true;
  });
}

export const ORPHAN_TOOL_RESULT_CONTENT_FOR_TEST = ORPHAN_TOOL_RESULT_CONTENT;
